import { useMutation } from '@tanstack/react-query';

import { QUERY_KEYS } from 'api/constants';
import { resolve } from 'api/resolve';
import type { UserSettingsModel } from 'api/user-settings/types';
import { useUpdateByQueryKey } from 'hooks/useUpdateByQueryKey';
import { SNACKBAR_STORE } from 'state/snackbar/snackbarStore';

import { useUserSettingsApi } from '../user-settings-api';

interface UpdateUserSettingsOptions {
  /** Skip the error snackbar, for background writes the user never asked for. `useApi` still logs the failure. */
  silent?: boolean;
}

export const useUpdateUserSettings = ({ silent = false }: UpdateUserSettingsOptions = {}) => {
  const { updateUserSettings } = useUserSettingsApi();
  const updateByQueryKey = useUpdateByQueryKey();

  return useMutation({
    // The server reads, merges and saves the whole settings document, so overlapping updates can overwrite each other.
    scope: { id: 'user-settings' },
    mutationFn: (payload: Partial<UserSettingsModel>) => resolve(updateUserSettings(payload)),
    onSuccess: (result) => {
      updateByQueryKey([QUERY_KEYS.USER_SETTINGS], result);
    },
    onError: () => {
      if (silent) {
        return;
      }

      SNACKBAR_STORE.getState().showSnackbar('Failed to update user settings', 'error', true);
    }
  });
};
